import { cn } from '#app/utils/misc.tsx'

/**
 * The Group Count mark: three people, the center one highlighted, with three
 * attendance rays for the "Here!" moment. Flat colors only, per the brand
 * guidelines (no gradients, shadows or outlines).
 *
 * `tone="mono"` draws everything in the current text color for one-color use.
 */
export function LogoMark({
	className,
	tone = 'color',
	title,
}: {
	className?: string
	tone?: 'color' | 'mono'
	title?: string
}) {
	const mono = tone === 'mono'
	return (
		<svg
			viewBox="0 0 200 156"
			className={cn('h-auto shrink-0', className)}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role={title ? 'img' : undefined}
			aria-label={title}
			aria-hidden={title ? undefined : true}
		>
			<g
				stroke={mono ? 'currentColor' : 'var(--color-brand-orange)'}
				strokeWidth="9"
				strokeLinecap="round"
			>
				<path d="M100 24V6" />
				<path d="M67 33L56 18" />
				<path d="M133 33L144 18" />
			</g>
			<g fill={mono ? 'currentColor' : 'var(--color-brand-blue)'}>
				<circle cx="43" cy="68" r="20" />
				<path d="M8 131C8 106 23 91 43 91C63 91 78 106 78 131V143H8V131Z" />
				<circle cx="157" cy="68" r="20" />
				<path d="M122 131C122 106 137 91 157 91C177 91 192 106 192 131V143H122V131Z" />
			</g>
			<g fill={mono ? 'currentColor' : 'var(--color-brand-teal)'}>
				<circle cx="100" cy="59" r="23" />
				<path d="M58 137C58 107 76 87 100 87C124 87 142 107 142 137V151H58V137Z" />
			</g>
		</svg>
	)
}

function Wordmark({
	className,
	mono,
}: {
	className?: string
	mono?: boolean
}) {
	return (
		<span
			className={cn(
				'font-brand flex flex-col font-extrabold tracking-tight',
				className,
			)}
		>
			<span className={mono ? undefined : 'text-brand-navy dark:text-white'}>
				Youth
			</span>
			<span className={mono ? undefined : 'text-brand-teal'}>Rollcall</span>
		</span>
	)
}

/** Primary horizontal lockup: mark on the left, two-line wordmark on the right. */
export function Logo({
	className,
	tone = 'color',
}: {
	className?: string
	tone?: 'color' | 'mono'
}) {
	return (
		<span
			className={cn('inline-flex items-center gap-2.5', className)}
			role="img"
			aria-label="Youth Rollcall"
		>
			<LogoMark className="w-11" tone={tone} />
			<Wordmark className="text-[1.375rem] leading-[1.05]" mono={tone === 'mono'} />
		</span>
	)
}

/** Stacked lockup for square layouts and splash screens. */
export function LogoStacked({
	className,
	tone = 'color',
}: {
	className?: string
	tone?: 'color' | 'mono'
}) {
	return (
		<span
			className={cn('inline-flex flex-col items-center gap-2', className)}
			role="img"
			aria-label="Youth Rollcall"
		>
			<LogoMark className="w-20" tone={tone} />
			<Wordmark
				className="items-center text-3xl leading-[1.05]"
				mono={tone === 'mono'}
			/>
		</span>
	)
}
